import React, { useMemo, useRef } from 'react';
import isEmpty from 'lodash/isEmpty';
import { useRouter } from 'next/router';
import cx from 'utils/classnames';
import SectionTitle from 'components/blocks/headers/section-title/SectionTitle';
import Wysiwyg from 'components/blocks/wysiwyg/Wysiwyg';
import ProgramPager from 'components/sections/program-selection/programPager/ProgramPager';
import { usePagination } from 'hooks/custom/tables/usePagination';
import { ARTICLES_PAGE_LIMIT, QUERY_PARAMS } from 'consts';
import { scrollTo } from 'utils/generalUtils';
import { capitalizeFirstLetter } from 'utils/stringHelpers';
import type { ArticleType } from 'types';
import { Article } from './Article';
import { filterArticlesByTag, getUniqueArticleTags } from './articleTagHelpers';
import type { ArticlesDisplayMode } from './wpToArticlesSectionTransform';

const { ARTICLE_TAG_PARAM } = QUERY_PARAMS;

function getEmptyMessage(
  isFullList: boolean,
  selectedTagSlug?: string
): string {
  if (!isFullList) {
    return 'No articles selected.';
  }

  if (selectedTagSlug) {
    return 'No articles found for this tag.';
  }

  return 'No articles published yet.';
}

export interface ArticlesSectionProps {
  articles?: ArticleType[];
  title?: string;
  subTitle?: string;
  content?: string;
  variation?: string;
  background?: string;
  customCssClass?: string;
  displayMode?: ArticlesDisplayMode;
  postsPerPage?: number;
}

export function ArticlesSection({
  articles = [],
  title = '',
  subTitle = '',
  content = '',
  variation = '',
  background = '',
  customCssClass = '',
  displayMode = 'curated',
  postsPerPage = ARTICLES_PAGE_LIMIT,
}: ArticlesSectionProps): React.JSX.Element {
  const isFullList = displayMode === 'all';
  const articlesRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const selectedTagSlug =
    isFullList && typeof router.query[ARTICLE_TAG_PARAM] === 'string'
      ? router.query[ARTICLE_TAG_PARAM]
      : undefined;

  const availableTags = useMemo(
    () => (isFullList ? getUniqueArticleTags(articles) : []),
    [articles, isFullList]
  );

  const filteredArticles = useMemo(
    () =>
      isFullList ? filterArticlesByTag(articles, selectedTagSlug) : articles,
    [articles, isFullList, selectedTagSlug]
  );

  let pageLimit = filteredArticles.length || 1;

  if (isFullList) {
    pageLimit = postsPerPage > 0 ? postsPerPage : ARTICLES_PAGE_LIMIT;
  }

  const {
    state: { currentPageRecords, currentPageIndex, pageCount },
    goToPage,
  } = usePagination({
    records: filteredArticles,
    pageLimit,
  });

  const listArticles = isFullList ? currentPageRecords : filteredArticles;

  const onTagSelect = (tagSlug?: string): void => {
    const query = { ...router.query };

    if (tagSlug) {
      query[ARTICLE_TAG_PARAM] = tagSlug;
    } else {
      delete query[ARTICLE_TAG_PARAM];
    }

    router.push({ pathname: router.pathname, query }, undefined, {
      shallow: true,
    });
  };

  const onPagerClick = (pageIndex: number): void => {
    goToPage(pageIndex);

    if (articlesRef.current) {
      scrollTo(articlesRef.current.offsetTop);
    }
  };

  const articlesClass = cx({
    articles: true,
    [`articles--variation${capitalizeFirstLetter(variation)}`]: !!variation,
    [`articles--background${capitalizeFirstLetter(background)}`]: !!background,
    [`articles--${customCssClass}`]: !!customCssClass,
  });

  return (
    <div className={articlesClass} ref={articlesRef}>
      <div className="articles__inner">
        {(title || subTitle || content) && (
          <div className="articles__header">
            {title && (
              <div className="articles__title">
                <SectionTitle title={title} />
              </div>
            )}
            {subTitle && (
              <div className="articles__subTitle">
                <Wysiwyg content={subTitle} />
              </div>
            )}
            {content && (
              <div className="articles__copy">
                <Wysiwyg content={content} />
              </div>
            )}
          </div>
        )}
        {isFullList && !isEmpty(availableTags) && (
          <div className="articles__tagFilter">
            <span className="articles__tagFilterLabel">Filter by tag:</span>
            <ul className="articles__tagList">
              <li className="articles__tagListItem">
                <button
                  type="button"
                  className={`articles__tagButton${
                    !selectedTagSlug ? ' articles__tagButton--active' : ''
                  }`}
                  onClick={() => onTagSelect()}
                >
                  All
                </button>
              </li>
              {availableTags.map((tag) => (
                <li
                  key={`articleTag_${tag.slug}`}
                  className="articles__tagListItem"
                >
                  <button
                    type="button"
                    className={`articles__tagButton${
                      selectedTagSlug === tag.slug
                        ? ' articles__tagButton--active'
                        : ''
                    }`}
                    onClick={() => onTagSelect(tag.slug)}
                  >
                    {tag.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        {isEmpty(filteredArticles) && (
          <p className="articles__empty">
            {getEmptyMessage(isFullList, selectedTagSlug)}
          </p>
        )}
        {!isEmpty(filteredArticles) && (
          <>
            <ul
              className="articles__list"
              key={
                isFullList
                  ? `articles-${selectedTagSlug ?? 'all'}-${currentPageIndex}`
                  : 'articles-curated'
              }
            >
              {listArticles.map((article: ArticleType) => (
                <li
                  key={`articleItem_${article.slug}`}
                  className="articles__listItem"
                >
                  <Article {...article} />
                </li>
              ))}
            </ul>
            {isFullList && (
              <div className="articles__pager">
                <ProgramPager
                  pageCount={pageCount}
                  onPagerClick={onPagerClick}
                  currentPageIndex={currentPageIndex}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
